import { z } from 'zod';
import { BusinessDateSchema } from './encoding.js';
/**
 * Employee Happiness Meter contracts (FRD-HRP-083 to HRP-089; CLR-44): a person's own rating for
 * today, and the results.
 *
 * No response names a person: a rating is anonymous in the results (FRD-HRP-086), so the results
 * carry counts and comments only, and a comment carries its date but no time and no identifier.
 */
export const HappinessRatingValueSchema = z.enum(['happy', 'neutral', 'unhappy']);
const CommentSchema = z.string().trim().min(1).max(500);
/** The signed-in person's own rating for today's business date; `rating` is null until given. */
export const MyHappinessSchema = z.object({
    today: BusinessDateSchema,
    rating: HappinessRatingValueSchema.nullable(),
    comment: z.string().nullable(),
});
/** Gives today's rating, or replaces it. A missing or null comment clears the comment. */
export const SetMyHappinessRequestSchema = z.object({
    rating: HappinessRatingValueSchema,
    comment: CommentSchema.nullable().optional(),
});
/** The period of the results; both ends are inclusive business dates. */
export const HappinessResultsQuerySchema = z.object({
    from: BusinessDateSchema.optional(),
    to: BusinessDateSchema.optional(),
});
const CountsSchema = z.object({
    happy: z.number().int().nonnegative(),
    neutral: z.number().int().nonnegative(),
    unhappy: z.number().int().nonnegative(),
});
export const HappinessResultsSchema = z.object({
    from: BusinessDateSchema,
    to: BusinessDateSchema,
    /** Every rating in the period, by face. */
    counts: CountsSchema,
    /** The days of the period on which at least one rating was given, newest first. */
    days: z.array(CountsSchema.extend({ date: BusinessDateSchema })),
    /** The comments given in the period, newest day first. Never a name, a time or an identifier. */
    comments: z.array(z.object({
        ratedOn: BusinessDateSchema,
        rating: HappinessRatingValueSchema,
        comment: z.string(),
    })),
    /** True when the period holds more comments than are returned. */
    commentsTruncated: z.boolean(),
});
/**
 * Feedback on actions (FRD-CORE-062 to CORE-066; CLR-61): right after submitting a request, a
 * person may rate how it went, or decline. Anonymous in the results like the meter.
 */
export const FEEDBACK_ACTIONS = [
    'leave_request',
    'employee_request',
    'purchase_request',
    'expense_claim',
];
export const FeedbackActionSchema = z.enum(FEEDBACK_ACTIONS);
/** Whether the caller has already answered the offer for this action today (rated or declined). */
export const ActionFeedbackStatusSchema = z.object({
    action: FeedbackActionSchema,
    today: BusinessDateSchema,
    answered: z.boolean(),
});
/** Rates the action just done, or declines (`rating: null`, "Not now"; no comment then). */
export const AnswerActionFeedbackRequestSchema = z
    .object({
    rating: HappinessRatingValueSchema.nullable(),
    comment: CommentSchema.nullable().optional(),
})
    .refine((value) => value.rating !== null || (value.comment ?? null) === null, {
    message: 'A declined offer carries no comment',
    path: ['comment'],
});
export const ActionFeedbackResultsSchema = z.object({
    from: BusinessDateSchema,
    to: BusinessDateSchema,
    /** Every kind of action, with its counts by face in the period (declined offers not counted). */
    actions: z.array(CountsSchema.extend({ action: FeedbackActionSchema })),
    /** The comments of the period, newest day first. Never a name, a time or an identifier. */
    comments: z.array(z.object({
        answeredOn: BusinessDateSchema,
        action: FeedbackActionSchema,
        rating: HappinessRatingValueSchema,
        comment: z.string(),
    })),
    commentsTruncated: z.boolean(),
});
//# sourceMappingURL=happiness.js.map